import { Outlet, NavLink, useNavigate, useLocation } from "react-router";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth";
import {
  LayoutDashboard,
  Signal,
  Map,
  FlaskConical,
  BarChart3,
  TrendingUp,
  Wallet,
  FileText,
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Terminal,
  Ticket,
  MoreHorizontal,
  X,
  Sunrise,
  Star,
  Search,
  Newspaper,
  ArrowDownUp,
  Activity,
} from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { CommandPalette } from "./CommandPalette";
import { QuickActionBar } from "./QuickActionBar";
import { useRegime } from "@/lib/hooks";
import { useKeyboardShortcuts } from "@/lib/useKeyboardShortcuts";

const NAV_GROUPS = [
  {
    label: "Trading",
    items: [
      { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
      { path: "/brief", label: "Brief", icon: Sunrise },
      { path: "/signals", label: "Signals", icon: Signal },
      { path: "/portfolio", label: "Portfolio", icon: Wallet },
      { path: "/trades", label: "Trades", icon: ArrowDownUp },
      { path: "/watchlist", label: "Watchlist", icon: Star },
    ],
  },
  {
    label: "Analysis",
    items: [
      { path: "/research", label: "Research", icon: FlaskConical },
      { path: "/options", label: "Options", icon: Activity },
      { path: "/backtests", label: "Backtests", icon: BarChart3 },
    ],
  },
  {
    label: "Intelligence",
    items: [
      { path: "/market", label: "Market Map", icon: Map },
      { path: "/macro", label: "Macro", icon: TrendingUp },
      { path: "/reports", label: "Reports", icon: FileText },
      { path: "/intel", label: "Intel", icon: Newspaper },
    ],
  },
  {
    label: "Admin",
    items: [
      { path: "/system", label: "System", icon: Settings },
      { path: "/dev", label: "Console", icon: Terminal },
    ],
  },
];

// Primary tabs shown in the bottom bar
const MOBILE_TABS = [
  { path: "/dashboard", label: "Home", icon: LayoutDashboard },
  { path: "/signals", label: "Signals", icon: Signal },
  { path: "/portfolio", label: "Portfolio", icon: Wallet },
  { path: "/research", label: "Research", icon: FlaskConical },
];

// All remaining pages go in the "More" drawer
const MORE_ITEMS = [
  { path: "/brief", label: "Brief", icon: Sunrise, admin: false },
  { path: "/trades", label: "Trades", icon: ArrowDownUp, admin: false },
  { path: "/watchlist", label: "Watchlist", icon: Star, admin: false },
  { path: "/backtests", label: "Backtests", icon: BarChart3, admin: false },
  { path: "/options", label: "Options", icon: Activity, admin: false },
  { path: "/market", label: "Market Map", icon: Map, admin: false },
  { path: "/macro", label: "Macro", icon: TrendingUp, admin: false },
  { path: "/reports", label: "Reports", icon: FileText, admin: false },
  { path: "/intel", label: "Intel", icon: Newspaper, admin: false },
  { path: "/system", label: "System", icon: Settings, admin: true },
  { path: "/dev", label: "Console", icon: Terminal, admin: true },
];

export function Layout() {
  const [collapsed, setCollapsed] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const regime = useRegime();

  // Keyboard shortcuts (number keys for nav, 'r' for refresh, '/' for palette)
  useKeyboardShortcuts({ onOpenPalette: () => setPaletteOpen(true) });

  // Ctrl+K to open command palette
  const handleGlobalKey = useCallback((e: KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "k") {
      e.preventDefault();
      setPaletteOpen((v) => !v);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", handleGlobalKey);
    return () => document.removeEventListener("keydown", handleGlobalKey);
  }, [handleGlobalKey]);

  // Check if current path is in the "More" section (for highlighting the More tab)
  const isMoreActive = MORE_ITEMS.some((item) => location.pathname.startsWith(item.path));

  return (
    <div className="flex h-dvh overflow-hidden noise">
      {/* Sidebar — hidden on mobile */}
      <aside
        className={cn(
          "hidden md:flex flex-col border-r border-white/[0.06] bg-[var(--color-sidebar)] transition-all duration-300 ease-out relative",
          collapsed ? "w-[68px]" : "w-[220px]",
        )}
      >
        {/* Ambient glow at top */}
        <div className="absolute top-0 left-0 right-0 h-40 bg-gradient-to-b from-amber-500/[0.08] to-transparent pointer-events-none" />

        {/* Logo */}
        <div className={cn("flex items-center gap-3 px-5 py-5 border-b border-white/[0.06] relative", collapsed && "px-0 justify-center")}>
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center text-white font-bold text-sm shrink-0 shadow-lg shadow-amber-500/25">
            M
          </div>
          {!collapsed && (
            <span className="font-bold text-amber-500 text-xl tracking-tight">
              MIDAS
            </span>
          )}
        </div>

        {/* Search trigger */}
        <button
          onClick={() => setPaletteOpen(true)}
          className={cn(
            "flex items-center gap-3 mx-2.5 mt-3 px-3 py-2 rounded-lg text-[13px] font-medium text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.04] transition-all duration-150",
            collapsed && "justify-center px-0 mx-1.5",
          )}
        >
          <Search size={18} className="shrink-0" />
          {!collapsed && (
            <>
              <span>Search</span>
              <kbd className="ml-auto text-[10px] font-mono text-zinc-600 bg-white/[0.06] px-1.5 py-0.5 rounded">⌘K</kbd>
            </>
          )}
        </button>

        {/* Nav */}
        <nav className="flex-1 py-3 overflow-y-auto px-2.5">
          {NAV_GROUPS.filter((group) => group.label !== "Admin" || user?.role === "admin").map((group) => (
            <div key={group.label} className="mb-1">
              {!collapsed && (
                <div className="px-3 pt-4 pb-2 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-600">
                  {group.label}
                </div>
              )}
              {collapsed && group.label !== "Trading" && (
                <div className="mx-3 my-2 border-t border-white/[0.04]" />
              )}
              <div className="space-y-0.5">
                {group.items.map(({ path, label, icon: Icon }) => (
                  <NavLink
                    key={path}
                    to={path}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] font-medium transition-all duration-150",
                        isActive
                          ? "bg-white/[0.08] text-zinc-100 shadow-sm"
                          : "text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.04]",
                        collapsed && "justify-center px-0",
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <Icon size={18} className={cn("shrink-0", isActive && "text-amber-400")} />
                        {!collapsed && <span>{label}</span>}
                        {!collapsed && path === "/dashboard" && regime.data?.data && (() => {
                          const r = regime.data.data.regime;
                          const dotColor = r === "bull" ? "bg-emerald-400" : r === "bear" ? "bg-red-400" : r === "transition" ? "bg-amber-400" : "bg-purple-400";
                          return <div className={cn("ml-auto w-2 h-2 rounded-full", dotColor)} />;
                        })()}
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}

          {/* Admin: Invites */}
          {user?.role === "admin" && (
            <NavLink
              to="/invites"
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] font-medium transition-all duration-150 mt-0.5",
                  isActive
                    ? "bg-white/[0.08] text-zinc-100 shadow-sm"
                    : "text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.04]",
                  collapsed && "justify-center px-0",
                )
              }
            >
              {({ isActive }) => (
                <>
                  <Ticket size={18} className={cn("shrink-0", isActive && "text-amber-400")} />
                  {!collapsed && <span>Invites</span>}
                </>
              )}
            </NavLink>
          )}
        </nav>

        {/* User + Logout */}
        <div className="border-t border-white/[0.06] px-4 py-4">
          {!collapsed && (
            <div className="text-[11px] font-bold text-zinc-500 mb-2.5 truncate uppercase tracking-wider">{user?.username}</div>
          )}
          <button
            onClick={async () => {
              await logout();
              navigate("/login");
            }}
            className={cn(
              "flex items-center gap-2 text-[12px] font-medium text-zinc-500 hover:text-red-400 transition-colors duration-200",
              collapsed && "justify-center",
            )}
          >
            <LogOut size={14} />
            {!collapsed && <span>Sign Out</span>}
          </button>
        </div>

        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="flex items-center justify-center py-3 border-t border-white/[0.06] text-zinc-600 hover:text-zinc-400 transition-colors duration-200"
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto overscroll-contain bg-[var(--color-bg)]">
        <div className="p-4 md:p-6 pb-28 md:pb-6 max-w-[1800px] mx-auto animate-fade-in">
          <QuickActionBar onSearchClick={() => setPaletteOpen(true)} />
          <Outlet />
        </div>
      </main>

      {/* Mobile bottom tab bar */}
      <nav className="fixed bottom-0 inset-x-0 md:hidden bg-[var(--color-sidebar)] z-50 shadow-[0_-4px_20px_rgba(0,0,0,0.4)]" style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}>
        <div className="flex items-center justify-around px-2 py-1">
          {MOBILE_TABS.map(({ path, label, icon: Icon }) => {
            const isActive = location.pathname.startsWith(path);
            return (
              <NavLink
                key={path}
                to={path}
                className="flex flex-col items-center gap-0.5 py-2 px-3 min-w-[60px]"
              >
                <Icon size={20} className={cn("transition-colors", isActive ? "text-amber-400" : "text-zinc-500")} />
                <span className={cn("text-[10px] font-medium transition-colors", isActive ? "text-amber-400" : "text-zinc-500")}>
                  {label}
                </span>
              </NavLink>
            );
          })}

          {/* More button */}
          <button
            onClick={() => setMoreOpen(!moreOpen)}
            className="flex flex-col items-center gap-0.5 py-2 px-3 min-w-[60px]"
          >
            {moreOpen ? (
              <X size={20} className="text-amber-400" />
            ) : (
              <MoreHorizontal size={20} className={cn("transition-colors", isMoreActive ? "text-amber-400" : "text-zinc-500")} />
            )}
            <span className={cn("text-[10px] font-medium transition-colors", isMoreActive || moreOpen ? "text-amber-400" : "text-zinc-500")}>
              More
            </span>
          </button>
        </div>
      </nav>

      {/* More drawer — slides up from bottom bar */}
      {moreOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setMoreOpen(false)}
          />
          {/* Drawer */}
          <div className="fixed bottom-[60px] inset-x-0 z-50 md:hidden bg-[var(--color-surface-1)] rounded-t-2xl shadow-[0_-8px_30px_rgba(0,0,0,0.5)] p-4 animate-fade-in">
            <div className="grid grid-cols-3 gap-2">
              {MORE_ITEMS.filter((item) => !item.admin || user?.role === "admin").map(({ path, label, icon: Icon }) => {
                const isActive = location.pathname.startsWith(path);
                return (
                  <NavLink
                    key={path}
                    to={path}
                    onClick={() => setMoreOpen(false)}
                    className={cn(
                      "flex flex-col items-center gap-1.5 py-3 rounded-xl transition-colors",
                      isActive
                        ? "bg-amber-500/10 text-amber-400"
                        : "text-zinc-400 hover:bg-white/[0.04]",
                    )}
                  >
                    <Icon size={22} />
                    <span className="text-[11px] font-medium">{label}</span>
                  </NavLink>
                );
              })}
              {/* Invites for admin */}
              {user?.role === "admin" && (
                <NavLink
                  to="/invites"
                  onClick={() => setMoreOpen(false)}
                  className={cn(
                    "flex flex-col items-center gap-1.5 py-3 rounded-xl transition-colors",
                    location.pathname === "/invites"
                      ? "bg-amber-500/10 text-amber-400"
                      : "text-zinc-400 hover:bg-white/[0.04]",
                  )}
                >
                  <Ticket size={22} />
                  <span className="text-[11px] font-medium">Invites</span>
                </NavLink>
              )}
            </div>
            {/* Sign out in drawer */}
            <button
              onClick={async () => {
                setMoreOpen(false);
                await logout();
                navigate("/login");
              }}
              className="flex items-center justify-center gap-2 w-full mt-3 py-3 rounded-xl text-zinc-500 hover:text-red-400 transition-colors text-xs font-medium border-t border-white/[0.06]"
            >
              <LogOut size={14} />
              Sign Out
            </button>
          </div>
        </>
      )}

      {/* Command Palette */}
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}
